import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Car,
    Users,
    DollarSign,
    ShieldCheck,
    Wrench,
    LineChart,
    Package,
    Truck,
    FileCheck,
    Archive,
    LayoutDashboard,
    Gavel,
    CreditCard,
} from "lucide-react";
import Earth from "@/components/ui/globe";

export default function HomePage() {
    const ecosystemLinks = [
        {
            icon: Car,
            title: "Dealers",
            desc: "Manage inventory, sales, and staff with real-time syncing across locations.",
            to: "/dealers",
            color: "from-sky-500 to-sky-700",
        },
        {
            icon: Users,
            title: "Customers",
            desc: "Set your buying preferences and get matched with verified dealers.",
            to: "/customers",
            color: "from-emerald-500 to-emerald-700",
        },
        {
            icon: DollarSign,
            title: "Finance & Floorplan",
            desc: "Approve or fund deals directly from your portal with full transparency.",
            to: "/finance",
            color: "from-yellow-500 to-amber-600",
        },
        {
            icon: ShieldCheck,
            title: "Warranty & Insurance",
            desc: "Connect coverage programs to customer deals instantly and track claims.",
            to: "/warranty",
            color: "from-blue-500 to-indigo-600",
        },
        {
            icon: Wrench,
            title: "Service & Parts",
            desc: "Let service departments, technicians, and parts managers run their operations seamlessly.",
            to: "/service",
            color: "from-orange-500 to-red-500",
        },
        {
            icon: LineChart,
            title: "Dealer Groups & Analytics",
            desc: "Oversee multiple stores, track KPIs, and manage enterprise dealer networks.",
            to: "/dealergroups",
            color: "from-violet-500 to-purple-600",
        },
    ];

    return (
        <div className="relative min-h-screen bg-gradient-to-b from-black via-slate-900 to-slate-950 text-white overflow-hidden">
           
            {/* HERO */}
            <section className="relative z-10 flex flex-col items-center justify-center text-center py-24 px-6">
                <motion.h1
                    className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-sky-400 to-emerald-400"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    IDS<span className="text-sky-400">crm</span>
                </motion.h1>

                <motion.p
                    className="text-lg md:text-2xl max-w-2xl text-gray-300 mb-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 1 }}
                >
                    The <span className="font-semibold text-white">Dealer Operating System</span> — connecting sales, service,
                    finance & marketing in one intelligent ecosystem.
                </motion.p>

                <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/customer/signup">
                        <Button size="lg" className="bg-sky-500 hover:bg-sky-600">
                            Customer Sign Up
                        </Button>
                    </Link>

                    <Link to="/dealer/signup">
                        <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                            Dealer Sign Up
                        </Button>
                    </Link>

                    <Link to="/sales/login">
                        <Button
                            size="lg"
                            variant="outline"
                            className="border-white/30 text-white hover:bg-white/10"
                        >
                            Sales Login
                        </Button>
                    </Link>
                </div>
            </section>

            {/* ECOSYSTEM GRID */}
            <section className="relative z-10 py-16 px-6 max-w-6xl mx-auto">
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
                        One Ecosystem. Every Automotive Role.
                    </h2>
                    <p className="text-gray-300 max-w-2xl mx-auto">
                        IDSCRM connects customers, dealers, lenders, warranty companies, and
                        service shops — creating one connected platform that powers the
                        entire automotive experience.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {ecosystemLinks.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + i * 0.1 }}
                        >
                            <Link to={f.to} className="group block h-full">
                                <Card className="h-full bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 group-hover:scale-[1.02]">
                                    <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                                        <div
                                            className={`p-3 rounded-full bg-gradient-to-br ${f.color} bg-opacity-20`}
                                        >
                                            <f.icon className="w-10 h-10 text-white drop-shadow" />
                                        </div>
                                        <h3 className="text-xl font-semibold group-hover:text-sky-400 transition">
                                            {f.title}
                                        </h3>
                                        <p className="text-sm text-gray-400">{f.desc}</p>
                                    </CardContent>
                                </Card>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* PORTAL DIRECTORY */}
            <section className="relative z-10 py-16 px-6 max-w-6xl mx-auto">
                <div className="text-center mb-10">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Staff Portal Logins</h2>
                    <p className="text-gray-400 max-w-xl mx-auto text-sm">
                        All IDSCRM portals in one place — sign in with your assigned credentials.
                    </p>
                </div>

                {[
                    {
                        group: "Service & Repair",
                        portals: [
                            { icon: Wrench,         label: "Service Advisor",  to: "/service/login",    desc: "Manage appointments & customer communication" },
                            { icon: Wrench,         label: "Service Manager",  to: "/service/login",    desc: "Oversee service department operations" },
                            { icon: Wrench,         label: "Technician",       to: "/service/login",    desc: "Work repair orders & technician queue" },
                        ],
                    },
                    {
                        group: "Parts Department",
                        portals: [
                            { icon: Package, label: "Parts Clerk",   to: "/parts/login", desc: "Process parts requests and orders" },
                            { icon: Package, label: "Parts Manager", to: "/parts/login", desc: "Manage parts inventory and team" },
                        ],
                    },
                    {
                        group: "External Partners",
                        portals: [
                            { icon: ShieldCheck,   label: "Insurance",     to: "/insurance/login",  desc: "Manage policies and claims" },
                            { icon: Truck,         label: "Transport",     to: "/transport/login",  desc: "Vehicle transport coordination" },
                            { icon: FileCheck,     label: "Warranty",      to: "/warranty/login",   desc: "Warranty claims and coverage" },
                            { icon: Car,           label: "Collision/Body",to: "/collision/login",  desc: "Body shop repair management" },
                            { icon: Archive,       label: "Salvage",       to: "/salvage/login",    desc: "Salvage vehicle processing" },
                            { icon: Gavel,         label: "Auction Staff", to: "/auction/login",    desc: "Auction management portal" },
                        ],
                    },
                    {
                        group: "Finance & Lending",
                        portals: [
                            { icon: CreditCard,      label: "Floor Plan Finance", to: "/floorplan/login", desc: "Dealer inventory credit lines"        },
                            { icon: LayoutDashboard, label: "Lenders",            to: "/lenders/login",   desc: "Review and fund submitted deals"       },
                        ],
                    },
                    {
                        group: "Back Office",
                        portals: [
                            { icon: LayoutDashboard, label: "Admin / Back Office", to: "/admin/login", desc: "System management and reporting" },
                        ],
                    },
                ].map(({ group, portals }) => (
                    <div key={group} className="mb-8">
                        <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">{group}</h3>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {portals.map(({ icon: Icon, label, to, desc }) => (
                                <Link
                                    key={label}
                                    to={to}
                                    className="group flex items-start gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 transition"
                                >
                                    <Icon className="h-5 w-5 shrink-0 text-indigo-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-white group-hover:text-sky-400 transition">{label}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            {/* CUSTOMER + DEALER CTA SPLIT */}
            <section className="py-16 px-6 max-w-5xl mx-auto grid md:grid-cols-2 gap-8 text-center">
                {[
                    {
                        title: "I’m a Customer",
                        desc: "Create your buying profile, toggle when you’re looking for a vehicle, and let dealers compete to earn your business.",
                        to: "/customer/login",
                        color: "bg-emerald-500 hover:bg-emerald-600",
                    },
                    {
                        title: "I’m a Dealer",
                        desc: "Join the IDSCRM dealer network, manage your team, and start pulling live customer leads from across the ecosystem.",
                        to: "/dealers/signup",
                        color: "bg-sky-500 hover:bg-sky-600",
                    },
                ].map((card) => (
                    <Link
                        key={card.title}
                        to={card.to}
                        className="group p-6 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition cursor-pointer"
                    >
                        <h3 className="text-2xl font-bold mb-2 group-hover:text-sky-400 transition">
                            {card.title}
                        </h3>
                        <p className="text-gray-400 mb-6">{card.desc}</p>
                        <Button className={`${card.color} text-white`}>
                            Get Started
                        </Button>
                    </Link>
                ))}
            </section>

            {/* FEATURE GRID */}
            <section className="relative z-10 py-16 px-6 max-w-6xl mx-auto">
                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        { icon: Car, title: "Inventory", desc: "Real-time vehicle listings synced across all dealer sites." },
                        { icon: Users, title: "Sales & CRM", desc: "Track leads, chat live, and close deals anywhere." },
                        { icon: DollarSign, title: "Finance", desc: "Instant approvals, credit apps, and lender matching." },
                        { icon: Wrench, title: "Service", desc: "RO management, technician workflows, and customer updates." },
                        { icon: ShieldCheck, title: "Warranty", desc: "Automate protection plans and claim submissions." },
                        { icon: LineChart, title: "Analytics", desc: "See dealer performance, trends, and live broadcast metrics." },
                    ].map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + i * 0.1 }}
                        >
                            <Card className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300">
                                <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                                    <f.icon className="w-10 h-10 text-sky-400" />
                                    <h3 className="text-xl font-semibold">{f.title}</h3>
                                    <p className="text-sm text-gray-400">{f.desc}</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* EARTH / GLOBAL */}
            <section>
                <div className="bg-background flex flex-col items-center justify-center overflow-hidden">
                    <article className="border-border-0 relative mx-auto my-8 max-w-[500px] rounded-xl p-5 text-center">
                        <div className="relative z-10">
                            <h1 className="text-7xl leading-[100%] font-semibold tracking-tighter mb-5">
                                IDScrm Global
                            </h1>
                            <Earth
                                baseColor={[0.2, 0.5, 1]}
                                markerColor={[0.4, 0.8, 1]}
                                glowColor={[0.3, 0.6, 1]}
                                dark={1.05}
                                scale={1.15}
                                diffuse={1.3}
                            />
                        </div>
                    </article>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative z-10 py-16 px-6 text-center bg-gradient-to-r from-sky-800/40 via-blue-700/40 to-cyan-600/40 backdrop-blur-sm border-t border-white/10">
                <h2 className="text-3xl font-bold mb-4">
                    Ready to Power Your Dealership?
                </h2>
                <p className="text-gray-300 mb-8 max-w-xl mx-auto">
                    Join IDSCRM and unify every department under one intelligent dealer
                    platform.
                </p>
                <Link to="/dealers/signup">
                    <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
                        Get Started Today
                    </Button>
                </Link>
            </section>

            {/* BACKGROUND FX */}
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.25),transparent_50%)]"></div>
        </div>
    );
}
